import React, { useState, useMemo, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Spinner } from '@chakra-ui/react';
import { getDaysLeft, getStatusPill, transformUserDetails, transformPlans } from '../../Library/helpers';
import PaymentModal from './PaymentModal';
import blockedSimIllu from '../../Assets/images/pngs/blocked-device.png';
import './style.css';
import PATHS from '../../Routes/paths';
import { SUBSCRIPTION_EXPIRED_DAYS_THRESHOLD, SUBSCRIPTION_SIM_BLOCK_THRESHOLD_DAYS_LEFT, SUBSCRIPTION_SIM_BLOCK_WARNING_THRESHOLD_DAYS_LEFT } from '../../Library/constants';
import { StoreContext } from '../../Library/contexts';

const VehicleDetail = () => {
  const { vehicleId } = useParams();
  const navigate = useNavigate();
  const { setHasExpiredSubs, ensureSubsWarrantyData } = useContext(StoreContext);

  const [apiRawData, setApiRawData] = useState(null);
  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [activeTab, setActiveTab] = useState('subscription');
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [paymentItem, setPaymentItem] = useState(null);
  const [paymentSucceeded, setPaymentSucceeded] = useState(false);
  const [copiedText, setCopiedText] = useState(null);

  const loadData = (data) => {
    const vehicles = transformUserDetails(data);
    setVehicle(vehicles.find(v => v.id === vehicleId) || null);
    setApiRawData(data);
    setLoading(false);
    setError(null);
  };

  useEffect(() => {
    setSelectedPlanId(null);
    setLoading(true);
    setError(null);
    ensureSubsWarrantyData({}, (err, data) => {
      if (err) {
        setError('Failed to load vehicle details');
        setLoading(false);
        return;
      }
      loadData(data);
    });
  }, [vehicleId, ensureSubsWarrantyData]);

  const plans = useMemo(() => {
    const all = transformPlans(apiRawData?.ajjas_sub_details);
    if (!vehicle) return all;
    if (vehicle.hwtyp != null) {
      const typed = all.filter(p => p._raw?.hwtyp === vehicle.hwtyp);
      if (typed.length > 0) return typed;
    }
    // Standard device or unrecognized hwtyp (e.g. GARUDA_LITE=1) — use generic plans
    const generic = all.filter(p => !p._raw?.hwtyp);
    return generic.length > 0 ? generic : all;
  }, [apiRawData, vehicle]);

  useEffect(() => {
    if (plans.length > 0 && !selectedPlanId) {
      setSelectedPlanId(plans[0].id);
    }
  }, [plans, selectedPlanId]);

  if (loading) {
    return (
      <div className="panel-main pw-page detail-page" style={{ alignItems: 'center', justifyContent: 'center' }}>
        <Spinner size="lg" color="var(--pw-color-primary)" />
        <p style={{ color: 'var(--pw-text-secondary)', marginTop: 16, fontSize: 14 }}>Loading vehicle details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="panel-main pw-page detail-page" style={{ alignItems: 'center', justifyContent: 'center', color: 'var(--pw-text-secondary)' }}>
        <p>{error}</p>
        <button className="btn btn-primary" style={{ marginTop: 16, padding: '10px 24px', fontSize: 14 }} onClick={() => navigate(PATHS.PLANS_AND_WARRANTY)}>
          Back to Vehicles
        </button>
      </div>
    );
  }

  if (!vehicle) {
    return (
      <div className="panel-main pw-page detail-page" style={{ alignItems: 'center', justifyContent: 'center', color: 'var(--pw-text-secondary)' }}>
        <p>Vehicle not found.</p>
      </div>
    );
  }

  const handleCopy = (text, type) => {
    navigator.clipboard.writeText(text);
    setCopiedText(type);
    setTimeout(() => setCopiedText(null), 2000);
  };

  const subDays = getDaysLeft(vehicle.subscriptionExpiry);
  const warDays = getDaysLeft(vehicle.warrantyExpiry);
  const subPill = getStatusPill(subDays);
  const warPill = getStatusPill(warDays, { showBlocked: false });
  const isSimBlocked = subDays > -999 && subDays <= SUBSCRIPTION_SIM_BLOCK_THRESHOLD_DAYS_LEFT;

  const subExpiryDisplay = vehicle.subscriptionExpiry
    ? new Date(vehicle.subscriptionExpiry).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
    : '—';
  const warExpiryDisplay = vehicle.warrantyExpiry
    ? new Date(vehicle.warrantyExpiry).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
    : '—';

  // Warranty pricing from real API data
  const warrantyPlans = warDays > 0
    ? (apiRawData?.ajjas_war_details || [])
    : (apiRawData?.ajjas_war_renewal || []);
  const warrantyPlanRaw = warrantyPlans[0];
  const deviceWarrOffer = (apiRawData?.warr_offers || []).find(
    o => o.did === vehicle.did || o.warranty?.did === vehicle.did
  );
  const warrantyPrice = deviceWarrOffer?.offer?.amt ?? warrantyPlanRaw?.amt;
  const warrantyOriginalPrice = warrantyPlanRaw?.amt;
  const hasWarrantyDiscount = warrantyPrice != null && warrantyOriginalPrice != null && warrantyPrice < warrantyOriginalPrice;

  const selectedPlan = plans.find(p => p.id === selectedPlanId) || plans[0];

  const daysLabel = (d) => {
    if (d <= -999) return '—';
    if (d > 0) return `${d} days left`;
    if (d === 0) return 'Expiring today';
    return `Expired ${Math.abs(d)} days ago`;
  };
  const daysColor = (d) => d <= -999 ? 'var(--pw-text-secondary)' : d > 15 ? '#34d399' : d > 0 ? '#fbbf24' : '#f87171';

  return (
    <div className="panel-main pw-page detail-page">
      <button className="detail-back" onClick={() => navigate(PATHS.PLANS_AND_WARRANTY)}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" /></svg>
        Back to All Vehicles
      </button>

      <div className="detail-header">
        <h1>{vehicle.displayName}</h1>
        <p>{vehicle.regNumber}</p>
      </div>

      {/* ─── TAB BAR ─── */}
      <div className="tab-bar">
        <button className={`tab-item ${activeTab === 'subscription' ? 'active' : ''}`} onClick={() => setActiveTab('subscription')}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
          Subscription
          <span className={`tab-pill pill-${subPill.color}`}>{subPill.text}</span>
        </button>
        <button className={`tab-item ${activeTab === 'warranty' ? 'active' : ''}`} onClick={() => setActiveTab('warranty')}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>
          Warranty
          <span className={`tab-pill pill-${warPill.color}`}>{warPill.text}</span>
        </button>
      </div>

      {/* ─── SUBSCRIPTION TAB ─── */}
      {activeTab === 'subscription' && (
        <div className="tab-content" key="sub">
          <div className="detail-section">
            <div className="detail-section-title">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
              Current Subscription
            </div>
            <div className="info-card">
              <div className="info-card-row">
                <span className="info-card-label">Current Plan</span>
                <span className="info-card-value">{vehicle.currentPlan}</span>
              </div>
              <div className="info-card-row">
                <span className="info-card-label">Expiry Date</span>
                <span className="info-card-value">{subExpiryDisplay}</span>
              </div>
              <div className="info-card-row" style={{ marginTop: 10 }}>
                <span className={`status-pill pill-${subPill.color}`}>{subPill.text}</span>
                <span style={{ fontWeight: 700, fontSize: 15, color: daysColor(subDays) }}>{daysLabel(subDays)}</span>
              </div>
            </div>

            {subDays < SUBSCRIPTION_SIM_BLOCK_WARNING_THRESHOLD_DAYS_LEFT && subDays > SUBSCRIPTION_SIM_BLOCK_THRESHOLD_DAYS_LEFT && (
              <div className="sim-block-warning">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>
                <div>
                  <div className="sim-block-title">Recharge Soon to Avoid Permanent SIM Blocking</div>
                  <div className="sim-block-desc">If SIM gets blocked you will not be able to use that SIM ever. Recharge immediately to keep your tracking active.</div>
                </div>
              </div>
            )}
          </div>

          {isSimBlocked ? (
            <div className="blocked-sim-section">
              <div className="blocked-sim-card">
                <div className="blocked-sim-content">
                  <div className="blocked-sim-title">Device SIM has been blocked</div>
                  <div className="blocked-sim-desc">
                    SIM has been blocked because it was not recharged within 90 days.
                    Tracking and alerts are currently disabled for this vehicle.
                  </div>
                  <div className="blocked-sim-actions">
                    <div className="contact-us-msg">Contact us to replace your device SIM</div>
                    <div className="contact-options">
                      <div className="contact-chip-container">
                        <a href="tel:+918069502900" className="contact-chip phone">
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 11.9 19.79 19.79 0 0 1 1.61 3.28 2 2 0 0 1 3.6 1h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 8.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" /></svg>
                          +91 8069502900
                        </a>
                        <button className="copy-btn" onClick={() => handleCopy('+918069502900', 'phone')} title="Copy Phone">
                          {copiedText === 'phone'
                            ? <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                            : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>
                          }
                        </button>
                      </div>
                      <div className="contact-chip-container">
                        <a href="https://wa.me/918069502900" target="_blank" rel="noreferrer" className="contact-chip whatsapp">
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
                          WhatsApp
                        </a>
                        <button className="copy-btn" onClick={() => handleCopy('+918069502900', 'whatsapp')} title="Copy WhatsApp Number">
                          {copiedText === 'whatsapp'
                            ? <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                            : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>
                          }
                        </button>
                      </div>
                      <div className="contact-chip-container">
                        <a href="mailto:support@ajjas.com" className="contact-chip email">
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" /></svg>
                          support@ajjas.com
                        </a>
                        <button className="copy-btn" onClick={() => handleCopy('support@ajjas.com', 'email')} title="Copy Email">
                          {copiedText === 'email'
                            ? <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                            : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>
                          }
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="blocked-sim-illu-wrap">
                  <img src={blockedSimIllu} alt="Blocked SIM" className="blocked-sim-illu" />
                </div>
              </div>
            </div>
          ) : plans.length > 0 && selectedPlan ? (
            <div className="detail-section">
              <div className="detail-section-title">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" /><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" /></svg>
                Recharge Plans
              </div>

              {subDays > 0 && (
                <div style={{ fontSize: 13, color: 'var(--pw-color-primary)', background: 'rgba(33, 136, 255, 0.08)', padding: '10px 14px', borderRadius: 'var(--pw-radius-md)', marginBottom: 16, lineHeight: 1.4 }}>
                  💡 <strong>Good to know:</strong> Recharging early won't waste any remaining days — the new plan adds exactly {selectedPlan.durationMonths === 12 ? '1 year' : `${selectedPlan.durationMonths} months`} to your current expiry date.
                </div>
              )}

              <div className="plans-grid">
                {plans.map(plan => (
                  <div
                    key={plan.id}
                    className={`plan-card ${plan.isPopular ? 'popular' : ''} ${selectedPlanId === plan.id ? 'selected' : ''}`}
                    onClick={() => setSelectedPlanId(plan.id)}
                  >
                    {plan.isPopular && (
                      <div className="plan-badge">
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" /></svg>
                        Most Popular
                      </div>
                    )}
                    <div className="plan-title">{plan.title}</div>
                    <div className="plan-duration">{plan.duration}</div>
                    <div>
                      <span className="plan-price">₹{Math.round(plan.price / 1.18)}</span>
                      <span className="plan-gst-tag">+ GST</span>
                      {plan.originalPrice && <span className="plan-original-price">₹{plan.originalPrice}</span>}
                    </div>
                    {plan.savings && <div className="plan-savings">{plan.savings}</div>}
                    <ul className="plan-features">
                      {plan.features.map((f, i) => (
                        <li key={i}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--pw-color-primary)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>
                          {f}
                        </li>
                      ))}
                    </ul>
                    {plan.subtext && <div className="plan-subtext">{plan.subtext}</div>}
                  </div>
                ))}
              </div>

              <div className="sticky-cta">
                <div className="sticky-cta-info" style={{ minWidth: 0, flex: 1 }}>
                  <div className="sticky-label">Selected Plan</div>
                  <div className="sticky-value" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {selectedPlan.title} — ₹{Math.round(selectedPlan.price / 1.18)} + GST
                  </div>
                </div>
                <button
                  className="btn btn-primary"
                  style={{ flexShrink: 0, marginLeft: 16 }}
                  onClick={() => setPaymentItem({ title: selectedPlan.title, price: selectedPlan.price, originalPrice: selectedPlan.originalPrice, type: 'Subscription', planIdx: selectedPlan.idx, subs: vehicle._sub })}
                >
                  Continue
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>
                </button>
              </div>
            </div>
          ) : !isSimBlocked && (
            <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--pw-text-secondary)', fontSize: 14 }}>
              No recharge plans available.
            </div>
          )}
        </div>
      )}

      {/* ─── WARRANTY TAB ─── */}
      {activeTab === 'warranty' && (
        <div className="tab-content" key="war">
          <div className="detail-section">
            <div className="detail-section-title">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>
              Current Warranty
            </div>
            <div className="info-card">
              <div className="info-card-row">
                <span className="info-card-label">Warranty Expiry</span>
                <span className="info-card-value">{warExpiryDisplay}</span>
              </div>
              <div className="info-card-row" style={{ marginTop: 10 }}>
                <span className={`status-pill pill-${warPill.color}`}>{warPill.text}</span>
                <span style={{ fontWeight: 700, fontSize: 15, color: daysColor(warDays) }}>{daysLabel(warDays)}</span>
              </div>
            </div>
          </div>

          {warrantyPlanRaw ? (
            <div className="detail-section">
              <div className="detail-section-title">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" /><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" /></svg>
                {warDays > 0 ? 'Extend Warranty' : 'Renew Warranty'}
              </div>

              {warDays > 0 && (
                <div style={{ fontSize: 13, color: 'var(--pw-color-primary)', background: 'rgba(33, 136, 255, 0.08)', padding: '10px 14px', borderRadius: 'var(--pw-radius-md)', marginBottom: 16, lineHeight: 1.4 }}>
                  💡 <strong>Good to know:</strong> Extending early won't waste any remaining days — the new plan adds exactly 1 year to your current expiry date.
                </div>
              )}

              <div className="warranty-grid">
                <div className="warranty-pricing-card">
                  <div style={{ marginBottom: 6, fontSize: 13, color: 'var(--pw-text-secondary)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {warrantyPlanRaw.nm || '1 Year Extended Warranty'}
                  </div>
                  <div>
                    <span className="warranty-price-big">₹{warrantyPrice}</span>
                    {hasWarrantyDiscount && (
                      <span className="warranty-price-original">₹{warrantyOriginalPrice}</span>
                    )}
                  </div>
                  {hasWarrantyDiscount && (
                    <div className="warranty-tag green">
                      Save ₹{warrantyOriginalPrice - warrantyPrice}
                    </div>
                  )}
                  {warDays > 0 && hasWarrantyDiscount && (
                    <div style={{ marginTop: 14, fontSize: 13, color: 'var(--pw-color-accent)', fontWeight: 600 }}>
                      ⏳ Renew before expiry to save ₹{warrantyOriginalPrice - warrantyPrice}!
                    </div>
                  )}
                  <button
                    className="warranty-cta-btn"
                    onClick={() => setPaymentItem({ title: warrantyPlanRaw.nm || 'Extended Warranty', price: warrantyPrice, originalPrice: hasWarrantyDiscount ? warrantyOriginalPrice : undefined, type: 'Warranty', subs: vehicle._warranty })}
                  >
                    {warDays > 0 ? 'Extend' : 'Renew'} Warranty @ ₹{warrantyPrice}
                    {hasWarrantyDiscount && (
                      <span style={{ textDecoration: 'line-through', opacity: 0.6, marginLeft: 8, fontWeight: 400 }}>₹{warrantyOriginalPrice}</span>
                    )}
                  </button>
                </div>

                <div>
                  <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 16 }}>Why Extend Warranty?</div>
                  <ul className="warranty-benefits">
                    {[
                      { title: 'Guaranteed Replacement', desc: 'Free replacement in case of device failure', Icon: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" /><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" /></svg> },
                      { title: 'Save up to ₹1200', desc: 'On repair and servicing costs', Icon: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" /></svg> },
                      { title: 'Instant Support', desc: 'Priority customer support access', Icon: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 11.9 19.79 19.79 0 0 1 1.61 3.28 2 2 0 0 1 3.6 1h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 8.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" /></svg> },
                      { title: 'Free Extended Subscription', desc: 'No charges during device downtime', Icon: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg> },
                    ].map(({ title, desc, Icon }) => (
                      <li key={title} className="warranty-benefit-item">
                        <div className="warranty-benefit-icon"><Icon /></div>
                        <div>
                          <div style={{ fontWeight: 600 }}>{title}</div>
                          <div style={{ fontSize: 12, color: 'var(--pw-text-secondary)', marginTop: 2 }}>{desc}</div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--pw-text-secondary)', fontSize: 14 }}>
              No warranty plans available.
            </div>
          )}
        </div>
      )}

      {paymentItem && (
        <PaymentModal
          item={paymentItem}
          onClose={() => {
            setPaymentItem(null);
            if (paymentSucceeded) {
              setPaymentSucceeded(false);
              setLoading(true);
              ensureSubsWarrantyData({ forceRefresh: true }, (err, data) => {
                if (!err) {
                  const vehicles = transformUserDetails(data);
                  setVehicle(vehicles.find(v => v.id === vehicleId) || null);
                  setApiRawData(data);
                  setHasExpiredSubs(vehicles.some(v => {
                    const days = getDaysLeft(v.subscriptionExpiry);
                    return days <= SUBSCRIPTION_EXPIRED_DAYS_THRESHOLD && days > SUBSCRIPTION_SIM_BLOCK_THRESHOLD_DAYS_LEFT;
                  }));
                }
                setLoading(false);
              });
            }
          }}
          onSuccess={() => setPaymentSucceeded(true)}
        />
      )}
    </div>
  );
};

export default VehicleDetail;
